import React, { useEffect, useState } from "react";
import ColoredDot from "./coloredDot";
import { Branding } from "assets/icons";
import { getNumericValuesForBuilding } from "../utils/calculateNumericValuesForBuilding";
import {
  ArrowLineDown,
  ArrowSquareOut,
  Blueprint,
  Building,
  CurrencyEur,
  Drop,
  Elevator,
  Exam,
  Eye,
  File,
  FireExtinguisher,
  Gavel,
  House,
  Lightbulb,
  Lightning,
  LinkSimpleHorizontal,
  MapPinSimpleLine,
  Oven,
  Percent,
  Question,
  Radioactive,
  ShieldCheck,
  SquareLogo,
  User,
  UserCircleCheck,
  Wallet,
  WarningDiamond,
  Waves,
  Wind,
} from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";

interface OperationalCardBuildingProps {
  building: any;
  categories: { [key: string]: string[] };
  onCellClick: (building: any, header: string) => void;
}

const OperationalCardBuilding: React.FC<OperationalCardBuildingProps> = ({
  building,
  categories,
  onCellClick,
}) => {
  const [numericValuesMap, setNumericValuesMap] = useState<{ [key: string]: any }>({});
  const { session } = useSession();

  useEffect(() => {
    const fetchNumericValues = async () => {
      try {
        const newNumericValues: { [key: string]: any } = {};
        
        for (const [_, subCategories] of Object.entries(categories)) {
          for (const subCategory of subCategories) {
            const values = await getNumericValuesForBuilding(building, subCategory, session);
            newNumericValues[subCategory] = values;
          }
        }
        
        setNumericValuesMap(newNumericValues);
      } catch (error) {
        console.error('Error fetching numeric values:', error);
      } finally {
      }
    };

    fetchNumericValues();
  }, [building, categories, session]);

  const iconStyling = "h-[18px] w-[18px]";
  const iconWeight = "duotone";
  const iconSize = 18;
  const getIcon = (category: any) => {
    const icons = {
      "Vis. inspectie": <Eye weight={iconWeight} size={iconSize} />,
      Energieprestaties: <Lightbulb weight={iconWeight} size={iconSize} />,
      Elektriciteit: <Lightning weight={iconWeight} size={iconSize} />,
      Klimatisatie: <Wind weight={iconWeight} size={iconSize} />,
      Brandveiligheid: <FireExtinguisher weight={iconWeight} size={iconSize} />,
      Vergunning: <Exam weight={iconWeight} size={iconSize} />,
      Woonconformiteit: <House weight={iconWeight} size={iconSize} />,
      Verzekering: <UserCircleCheck weight={iconWeight} size={iconSize} />,
      Huurder: <User weight={iconWeight} size={iconSize} />,
      Huurcontract: <File weight={iconWeight} size={iconSize} />,
      Huurdersverzekering: <Exam weight={iconWeight} size={iconSize} />,
      Huurdersverplichtingen: (
        <UserCircleCheck weight={iconWeight} size={iconSize} />
      ),
      Plaatsbeschrijving: (
        <MapPinSimpleLine weight={iconWeight} size={iconSize} />
      ),
      Huurbetalingen: <Wallet weight={iconWeight} size={iconSize} />,
      Waarborg: <CurrencyEur weight={iconWeight} size={iconSize} />,
      Status: <Question weight={iconWeight} size={iconSize} />,
      Stabiliteit: <Waves weight={iconWeight} size={iconSize} />,
      Vergunningen: <Exam weight={iconWeight} size={iconSize} />,
      Waterhuishouding: <Drop weight={iconWeight} size={iconSize} />,
      Verzekeringen: <ShieldCheck weight={iconWeight} size={iconSize} />,
      Erfdienstbaarheden: (
        <WarningDiamond weight={iconWeight} size={iconSize} />
      ),
      "Yally verbeteringen": <Branding className={iconStyling} />,
      Website: <LinkSimpleHorizontal weight={iconWeight} size={iconSize} />,
      Whise: <ArrowSquareOut weight={iconWeight} size={iconSize} />,
      Stedenbouw: <SquareLogo weight={iconWeight} size={iconSize} />,
      Eigendom: <House weight={iconWeight} size={iconSize} />,
      Kadaster: <Gavel weight={iconWeight} size={iconSize} />,
      Taxatie: <Percent weight={iconWeight} size={iconSize} />,
      Toestellen: <Oven weight={iconWeight} size={iconSize} />,
      Lift: <Elevator weight={iconWeight} size={iconSize} />,
      Plannen: <Blueprint weight={iconWeight} size={iconSize} />,
      Bodem: <ArrowLineDown weight={iconWeight} size={iconSize} />,
      Litiges: <Gavel weight={iconWeight} size={iconSize} />,
      "Gevaarlijke stoffen": (
        <Radioactive weight={iconWeight} size={iconSize} />
      ),
    };
    return (icons as any)[category] || null;
  };

  return (
    <div className="relative overflow-hidden rounded-lg border-2 border-moss-primary bg-white bg-linear-to-b from-transparent via-transparent via-80% to-moss-primary/10 text-sm shadow-sm">
      <div className="relative z-1 flex flex-col divide-y divide-neutral-primary/20">
        <header className="flex items-center gap-1.5 px-3 py-2.5">
          <Building weight="duotone" size="18" />
          <h3 className="font-extrabold text-lg text-gray-900">
            {building.id} - Algemene delen
            </h3>
        </header>
        <ul className="flex flex-col divide-y divide-neutral-primary/10">
          {Object.entries(categories).map(([mainCategory, subCategories]) => {
            if (subCategories.length > 0) {
              return (
                <li
                  key={mainCategory}
                  className="divide-y divide-neutral-primary/10"
                >
                  <div className="flex justify-end bg-linear-to-b from-white/80 to-white/1 px-3 py-2.5 backdrop-blur-lg sm:static sm:top-0">
                    <span className="text-xs uppercase tracking-wider text-neutral-secondary-mid">
                      {mainCategory}
                    </span>
                  </div>
                  {subCategories.map((subCategory) => {
                    const numericValues = numericValuesMap[subCategory] || {
                      green: { count: 0, labels: [] },
                      orange: { count: 0, labels: [] },
                      red: { count: 0, labels: [] },
                      transparent: { count: 0, labels: [] },
                    };
                    const IconComponent = getIcon(subCategory);
                    return (
                      <ul className="flex flex-col divide-y divide-neutral-primary/10">
                        <li
                          key={subCategory}
                          className="flex cursor-pointer flex-wrap items-center justify-between gap-3 px-3 py-2.5 transition-all hover:bg-neutral-primary/5"
                          onClick={() => onCellClick(building, subCategory)}
                        >
                          <div className="flex items-center gap-2">
                            {IconComponent && <span>{IconComponent}</span>}
                            <span>{subCategory}</span>
                          </div>
                          <div className="flex cursor-pointer items-center gap-1.5">
                            <ColoredDot
                              color="green"
                              number={numericValues.green.count}
                              labels={numericValues.green.labels}
                            />
                            <ColoredDot
                              color="orange"
                              number={numericValues.orange.count}
                              labels={numericValues.orange.labels}
                            />
                            <ColoredDot
                              color="red"
                              number={numericValues.red.count}
                              labels={numericValues.red.labels}
                            />
                            <ColoredDot
                              color="transparent"
                              number={numericValues.transparent.count}
                              labels={numericValues.transparent.labels}
                            />
                          </div>
                        </li>
                      </ul>
                    );
                  })}
                </li>
              );
            }
            return null;
          })}
        </ul>
      </div>
      <div className="absolute bottom-0 right-0 z-0 translate-x-1/4 translate-y-1/4 text-moss-primary/12">
        <Building weight="duotone" size="120" />
      </div>
    </div>
  );
};

export default OperationalCardBuilding;
